import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Tooltip, Divider, Dialog, IconButton } from "@mui/material";

import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { useMemo } from "react";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import Spinner from "../../../../Component/Spinner/Spinner";
import MUIButton from "../../../../Component/UI/MUIButton/MUIButton";
import { OVERVIEW_LLM_LABELS } from "../../../../utils/constant";
import CloseIcon from "@mui/icons-material/Close";
import MUIToolTipComp from "../../../../Component/UI/ActionTooltipBrandAudit/MUIToolTip";

// Global color cache and pool
const dynamicLLMColorMap = {};
const colorPool = [
  "#C7F0EC",
  "#FF6F6F",
  "#CEB4FF",
  "#FFCE6E",
  "#92B7FE",
  "#FEE7E6",
  "#B0E57C",
  "#FFB6C1",
  "#ADD8E6",
  "#D8BFD8",
  "#FFD700",
  "#7FFFD4",
]; // Extend this if more unique colors are needed

let colorIndex = 0;


// ---------- small utils ----------
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const getMaxBarsAcross = (attributes) => {
  if (!attributes || attributes.length === 0) return 0;
  let maxBars = 0;
  for (const item of attributes) {
    const bars = Object.keys(item).filter((k) => k !== "name" && item[k]);
    if (bars.length > maxBars) maxBars = bars.length;
  }
  return maxBars;
};

const firstPageQuota = (maxBars) => {
  if (maxBars <= 1) return Number.POSITIVE_INFINITY; // all on page 1
  if (maxBars === 2) return 7;
  if (maxBars === 3) return 6;
  return 5; // 4 and above
};

function getColorForLLM(llm) {
  if (!dynamicLLMColorMap[llm]) {
    dynamicLLMColorMap[llm] = colorPool[colorIndex % colorPool.length];
    colorIndex++;
  }
  return dynamicLLMColorMap[llm];
}

// ---------- Reporting Modal (drives pagination) ----------
const ReportingAttributesModal = ({
  open,
  onClose,
  title,
  attributes,
}) => {
  const total = attributes?.length || 0;

  // decide slices from density
  const maxBars = useMemo(() => getMaxBarsAcross(attributes), [attributes]);
  const quota = useMemo(() => firstPageQuota(maxBars), [maxBars]);
  const slices = useMemo(() => {
    if (!attributes || attributes.length === 0) return [];
    if (quota === Number.POSITIVE_INFINITY || quota >= total) return [attributes];
    return [attributes.slice(0, quota), attributes.slice(quota)];
  }, [attributes, quota, total]);

  const [pageIndex, setPageIndex] = useState(0);

  // mark readiness for puppeteer (if you need later)
  useEffect(() => {
    const el = document.getElementById("report-capture");
    if (el) el.setAttribute("data-ready", "1");
    // }, [pageIndex, perPage, total]);
  }, [pageIndex, total]);

  useEffect(() => {
    if (open) setPageIndex(0);
  }, [open]);


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          p: 2,
          borderRadius: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {title} • Page {pageIndex + 1} / {slices.length}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* This is the exact capture area; adjust to your PPT content size if needed */}
      <Box
        id="report-capture"
        data-ready="0"
        sx={{
          width: 900,        // ≈ 9.2in * 96 (you can fine-tune)
          height: 650,       // ≈ 4.23in * 96
          overflow: "hidden",
          bgcolor: "#fff",
          border: "1px solid #eee",
          borderRadius: 1,
          p: 2,
        }}
      >
        <AttributeSection attributes={slices[pageIndex] || []} showValues />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="body2">
          Density: maxBars={maxBars} → first page quota={quota}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <MUIButton
            variant="outlined"
            size="small"
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            disabled={pageIndex === 0}
          >
            Prev
          </MUIButton>
          <MUIButton
            variant="contained"
            size="small"
            onClick={() =>
              setPageIndex((i) => Math.min(slices.length - 1, i + 1))
            }
            disabled={pageIndex === slices.length - 1}
          >
            Next
          </MUIButton>
        </Box>
      </Box>
    </Dialog>
  );
};

const AttributeSection = ({
  attributes,
  isNegative,
  selectedInput,
  sources,
  saveActions,
  isActionLoading,
  isCompetitor,
  showTooltip = false,
  getTooltipProps = () => { },
  showValues = false,
}) => {
  const sortedData = useMemo(() => {
    if (!attributes || attributes.length === 0) return;
    return [...attributes].sort((a, b) => {
      const totalA = Object.entries(a).reduce(
        (sum, [key, value]) =>
          key !== "name" ? sum + (parseFloat(value) || 0) : sum,
        0
      );
      const totalB = Object.entries(b).reduce(
        (sum, [key, value]) =>
          key !== "name" ? sum + (parseFloat(value) || 0) : sum,
        0
      );
      return totalB - totalA;
    });
  }, [attributes]);

  const llmKeys = useMemo(() => {
    if (!attributes || attributes.length === 0) return;
    const set = new Set();
    attributes.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== "name") set.add(key);
      });
    });
    return Array.from(set);
  }, [attributes]);

  const maxLLMValue = useMemo(() => {
    if (!attributes || attributes.length === 0) return;
    return Math.max(
      ...attributes.flatMap((item) =>
        llmKeys?.map((llm) => Number(item[llm]) || 0)
      ),
      1
    );
  }, [attributes, llmKeys]);

  const llmColorMap = useMemo(() => {
    const map = {};
    llmKeys?.forEach((llm, index) => {
      map[llm] = getColorForLLM(llm);
    });
    return map;
  }, [llmKeys]);

  return (
    <Box>
      <Box display="flex" mb={2} flexWrap="wrap" gap={2}>
        {llmKeys?.length > 1 && llmKeys?.map((llm, i) => (
          <Box key={llm} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 14,
                height: 14,
                backgroundColor: llmColorMap[llm],
                borderRadius: "50%",
              }}
            />
            <Typography variant="body2">
              {OVERVIEW_LLM_LABELS?.[llm] || llm}
            </Typography>
          </Box>
        ))}
      </Box>

      <Grid container spacing={2}>
        {sortedData?.map((attr, index) => {
          const entities = "";
          // Object.keys(sortedData?.[index])
          //   .filter((key) => {
          //     const isMandatory =
          //       key !== "name" && sortedData?.[index]?.[key] !== 0;
          //     const isConditionallyExcluded = isCompetitor
          //       ? key !== (selectedInput?.brand || selectedInput?.product)
          //       : true;

          //     return isMandatory && isConditionallyExcluded;
          //   })
          //   ?.join(", ");
          const mainEntity = selectedInput?.product ? "product" : "brand";
          const actionStatement = `${attr} is a Negative Attribute of the ${mainEntity} ${selectedInput?.brand || selectedInput?.product
            } `;
          return (
            <React.Fragment key={index}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box flex={1}>
                    {false ? (
                      <Tooltip
                        title={
                          <div
                            style={{
                              width: "220px",
                              padding: "10px",
                              background: "#222",
                              color: "#fff",
                              borderRadius: "5px",
                            }}
                          >
                            <Typography
                              style={{
                                borderBottom: "1px solid #dedede",
                                paddingBottom: "8px",
                              }}
                              variant="body2"
                              color="inherit"
                            >
                              {attr}
                            </Typography>

                            <Typography
                              style={{
                                borderBottom: "1px solid #dedede",
                                paddingBottom: "8px",
                              }}
                              variant="body2"
                              color="inherit"
                            >
                              Data Sources
                            </Typography>

                            {/* {sources &&
                              Object.values(sources)?.[0] &&
                              Object.keys(Object.values(sources)[0])
                                .slice(0, 5)
                                ?.map((link, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      color: "white",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {link}
                                  </Typography>
                                ))} */}
                            <hr />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginRight: "15px",
                              }}
                            >
                              {isActionLoading ? (
                                <Spinner />
                              ) : (
                                <MUIButton
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    saveActions(
                                      actionStatement,
                                      actionStatement,
                                      sources,
                                      entities
                                    )
                                  }
                                >
                                  <span
                                    style={{
                                      color: "white",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Add To Action
                                  </span>
                                </MUIButton>
                              )}
                            </div>
                          </div>
                        }
                        arrow
                      >
                        <Typography variant="body2">{attr.name}</Typography>
                      </Tooltip>
                    ) : (
                      showTooltip ?
                        <MUIToolTipComp
                          {...getTooltipProps(
                            isNegative ? "Negative Attribute" : "Positive Attribute",
                            attr,
                          )}>
                          <Typography variant="body2" style={{ cursor: "pointer" }}>{attr.name}</Typography>
                        </MUIToolTipComp>
                        :
                        <Typography variant="body2">{attr.name}</Typography>
                    )}
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    sx={{ marginTop: 1, marginBottom: 1 }}
                  >
                    {llmKeys
                      ?.filter((llm) => attr[llm])
                      ?.map((llm, i) => {
                        const value = Number(attr[llm]) || 0;
                        const barWidth = (value / maxLLMValue) * 450;

                        return (
                          <Tooltip
                            key={llm}
                            title={
                              <Typography fontSize={14}>
                                {OVERVIEW_LLM_LABELS?.[llm] || llm}: {value}
                              </Typography>
                            }
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                width: 400,
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  height: 12,
                                  width: barWidth,
                                  backgroundColor: llmColorMap[llm],
                                  borderRadius: 0.5,
                                  minWidth: 30,
                                  margin: 0,
                                  padding: 0,
                                  lineHeight: 0,
                                  cursor: "pointer",
                                }}
                              />
                              {showValues && (
                                <Typography component={"span"} variant="caption"
                                  sx={{
                                    ml: 2,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    height: 12,            // same as bar
                                    lineHeight: `12px`, // exactly one row
                                    whiteSpace: "nowrap",          // avoid wrapping -> no row growth
                                    m: 0,
                                    p: 0,
                                  }}
                                >
                                  {value}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
};

const AttributeBars = ({
  negativeAttributes = [],
  positiveAttributes = [],
  selectedInput,
  sources,
  saveActions,
  isActionLoading,
  isCompetitor,
  showTooltip = false,
  getTooltipProps = () => { },
  id = "Attributes_Overview"
}) => {
  // read ?reporting=true
  const search = typeof window !== "undefined" ? window.location.search : "";
  const reporting = useMemo(
    () => new URLSearchParams(search).get("reporting") === "true",
    [search]
  );

  // Modals
  const [openPos, setOpenPos] = useState(false);
  const [openNeg, setOpenNeg] = useState(false);

  // --------------------------
  // LOAD MORE STATE
  // --------------------------
  const INITIAL_COUNT = 10;
  const LOAD_MORE_STEP = 10;

  const [posVisibleCount, setPosVisibleCount] = useState(INITIAL_COUNT);
  const [negVisibleCount, setNegVisibleCount] = useState(INITIAL_COUNT);

  const visiblePositive = positiveAttributes.slice(0, posVisibleCount);
  const visibleNegative = negativeAttributes.slice(0, negVisibleCount);

  const showPosLoadMore = posVisibleCount < positiveAttributes.length;
  const showNegLoadMore = negVisibleCount < negativeAttributes.length;

  const loadMorePos = () => {
    setPosVisibleCount(prev => prev + LOAD_MORE_STEP);
  };

  const loadMoreNeg = () => {
    setNegVisibleCount(prev => prev + LOAD_MORE_STEP);
  };

  return (
    <div className="col-md-12 mt-4" id={id}>
      <div className="card p-3 custCard">
        <div className="row">
          <div className="col-md-12">
            <Box sx={{ mx: "auto" }}>

              {/* Reporting buttons */}
              {reporting && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mb: 2,
                  }}
                >
                  <MUIButton
                    id="open-report-positive"
                    variant="contained"
                    size="small"
                    onClick={() => setOpenPos(true)}
                  >
                    Generate Slides – Positive
                  </MUIButton>

                  <MUIButton
                    id="open-report-negative"
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenNeg(true)}
                  >
                    Generate Slides – Negative
                  </MUIButton>
                </Box>
              )}

              <Grid container spacing={6}>

                {/* POSITIVE SECTION */}
                <Grid item xs={6}>
                  <Typography variant="h6" align="left" gutterBottom>
                    Positive Attributes
                  </Typography>
                  <hr />

                  {!positiveAttributes?.length ? (
                    <EmptyView message="Data Not Available" />
                  ) : (
                    <>
                      <AttributeSection
                        attributes={visiblePositive}
                        showValues={reporting}
                        getTooltipProps={getTooltipProps}
                        showTooltip={showTooltip}
                      />

                      {showPosLoadMore && (
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                          <MUIButton
                            variant="outlined"
                            size="small"
                            onClick={loadMorePos}
                            style={{ border: 'none' }}
                          >
                            Load More
                          </MUIButton>
                        </Box>
                      )}
                    </>
                  )}
                </Grid>

                {/* NEGATIVE SECTION */}
                <Grid item xs={6}>
                  <Typography variant="h6" align="left" gutterBottom>
                    Negative Attributes
                  </Typography>
                  <hr />

                  {!negativeAttributes?.length ? (
                    <EmptyView message="Data Not Available" />
                  ) : (
                    <>
                      <AttributeSection
                        attributes={visibleNegative}
                        isNegative
                        selectedInput={selectedInput}
                        sources={sources}
                        saveActions={saveActions}
                        isActionLoading={isActionLoading}
                        isCompetitor={isCompetitor}
                        showValues={reporting}
                        getTooltipProps={getTooltipProps}
                        showTooltip={showTooltip}
                      />

                      {showNegLoadMore && (
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                          <MUIButton
                            variant="outlined"
                            size="small"
                            onClick={loadMoreNeg}
                            style={{ border: 'none' }}
                          >
                            Load More
                          </MUIButton>
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </div>
        </div>
      </div>

      {/* Reporting Modals */}
      {reporting && positiveAttributes?.length > 0 && (
        <ReportingAttributesModal
          open={openPos}
          onClose={() => setOpenPos(false)}
          title="Positive Attributes"
          attributes={positiveAttributes}
        />
      )}

      {reporting && negativeAttributes?.length > 0 && (
        <ReportingAttributesModal
          open={openNeg}
          onClose={() => setOpenNeg(false)}
          title="Negative Attributes"
          attributes={negativeAttributes}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default AttributeBars;

